import React from 'react';
import { Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import Icon from '../../../components/AppIcon';


const ComplianceTrendChart = ({ data, selectedCategory }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-md shadow-lg p-3">
          <p className="font-heading font-semibold text-sm text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 mb-1">
              <span className="caption text-xs" style={{ color: entry?.color }}>
                {entry?.name}:
              </span>
              <span className="data-text text-xs font-medium text-foreground">
                {entry?.value}%
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 w-full overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground mb-1 break-words">
            Compliance Trends Over Time
          </h3>
          <p className="caption text-xs md:text-sm text-muted-foreground break-words">
            Monthly compliance scores by regulation category
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Icon name="TrendingUp" size={16} color="var(--color-success)" />
          <span className="caption text-xs text-success whitespace-nowrap">+5.2% vs last quarter</span>
        </div>
      </div>

      <div className="w-full h-64 md:h-80 lg:h-96" aria-label="Compliance Trends Chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px', fontFamily: 'var(--font-caption)' }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px', fontFamily: 'var(--font-data)' }}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '12px', fontFamily: 'var(--font-caption)' }}
            />
            <Bar 
              dataKey="violations" 
              fill="var(--color-error)" 
              name="Active Violations"
              radius={[4, 4, 0, 0]}
            />
            <Line 
              type="monotone" 
              dataKey="overall" 
              stroke="var(--color-primary)" 
              strokeWidth={3}
              name="Overall Score"
              dot={{ fill: 'var(--color-primary)', r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="employment" 
              stroke="var(--color-accent)" 
              strokeWidth={2}
              name="Employment Act"
              dot={{ fill: 'var(--color-accent)', r: 3 }}
            />
            <Line 
              type="monotone" 
              dataKey="leave" 
              stroke="var(--color-success)" 
              strokeWidth={2}
              name="Leave Compliance"
              dot={{ fill: 'var(--color-success)', r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <p className="caption text-xs text-muted-foreground mb-1">Avg Score</p>
          <p className="data-text text-lg md:text-xl font-bold text-primary">92.5%</p>
        </div>
        <div className="text-center">
          <p className="caption text-xs text-muted-foreground mb-1">Best Month</p>
          <p className="data-text text-lg md:text-xl font-bold text-success">95.8%</p>
        </div>
        <div className="text-center">
          <p className="caption text-xs text-muted-foreground mb-1">Improvement</p>
          <p className="data-text text-lg md:text-xl font-bold text-accent">+8.3%</p>
        </div>
        <div className="text-center">
          <p className="caption text-xs text-muted-foreground mb-1">Target</p>
          <p className="data-text text-lg md:text-xl font-bold text-foreground">95.0%</p>
        </div>
      </div>
    </div>
  );
};

export default ComplianceTrendChart;